import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * Accounts Migration Console — super_admin only.
 *
 * Surfaces the state of the long-running properties → accounts
 * migration. Nothing here CHANGES live behavior — flipping a flag
 * here just toggles which code path is used; the old path is still
 * fully in place.
 *
 * Layout:
 *   1. Header + manual reconcile button
 *   2. Stats row (accounts vs properties counts + drift count)
 *   3. Flags table (toggleable)
 *   4. Audit log (paginated, severity filter)
 */
export default function MigrationConsolePage() {
  const { isSuperAdmin } = useAuth();

  const [stats, setStats] = useState(null);
  const [flags, setFlags] = useState([]);
  const [log, setLog] = useState({ entries: [], total: 0 });
  const [severityFilter, setSeverityFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reconcileMsg, setReconcileMsg] = useState('');

  const loadAll = useCallback(async () => {
    setError('');
    try {
      const [s, f, l] = await Promise.all([
        api.getMigrationStats(),
        api.getMigrationFlags(),
        api.getMigrationAuditLog({ limit: 100, severity: severityFilter || undefined }),
      ]);
      setStats(s);
      setFlags(f);
      setLog(l);
    } catch (e) {
      setError(e.message || 'Failed to load');
    }
  }, [severityFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!isSuperAdmin) {
    return (
      <div className="admin-page">
        <div className="page-header"><h2>Migration Console</h2></div>
        <p style={{ color: '#dc2626' }}>Super-admin only.</p>
      </div>
    );
  }

  async function toggleFlag(key, enabled) {
    if (busy) return;
    const confirm = window.confirm(
      `${enabled ? 'ENABLE' : 'DISABLE'} migration flag:\n\n${key}\n\nThis affects live behavior. Continue?`
    );
    if (!confirm) return;
    setBusy(true);
    try {
      await api.setMigrationFlag(key, enabled);
      await loadAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function runReconcile() {
    if (busy) return;
    setBusy(true);
    setReconcileMsg('Running…');
    try {
      const r = await api.runMigrationReconcile();
      setReconcileMsg(r.ok ? 'Reconcile complete — see audit log.' : `Failed: ${r.error}`);
      await loadAll();
    } catch (e) {
      setReconcileMsg(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function runBackfillRequests() {
    if (busy) return;
    const confirm = window.confirm(
      'Backfill requests.account_id from accounts.legacy_property_id?\n\n' +
      'Idempotent — only touches rows where account_id is currently NULL.\n' +
      'Safe to run any time. Required before flipping accounts_shadow_read_requests.'
    );
    if (!confirm) return;
    setBusy(true);
    setReconcileMsg('Backfilling requests…');
    try {
      const r = await api.backfillRequestsAccountIds();
      setReconcileMsg(
        r.ok
          ? `Backfill complete — scanned ${r.scanned}, updated ${r.updated}, missing account ${r.missing_account}.`
          : `Failed: ${r.error}`
      );
      await loadAll();
    } catch (e) {
      setReconcileMsg(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function runBackfillExpenses() {
    if (busy) return;
    const confirm = window.confirm(
      'Backfill expenses.account_id + expense_accounts.account_id?\n\n' +
      'Two-pass: expenses table + the multi-property junction.\n' +
      'Idempotent — only touches rows where account_id is currently NULL.\n' +
      'Required before flipping accounts_shadow_read_expenses.'
    );
    if (!confirm) return;
    setBusy(true);
    setReconcileMsg('Backfilling expenses…');
    try {
      const r = await api.backfillExpensesAccountIds();
      setReconcileMsg(
        r.ok
          ? `Backfill complete — expenses: ${r.expenses_updated}/${r.expenses_scanned} (missing ${r.expenses_missing}); junction: ${r.junction_updated}/${r.junction_scanned} (missing ${r.junction_missing}).`
          : `Failed: ${r.error}`
      );
      await loadAll();
    } catch (e) {
      setReconcileMsg(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function runBackfillAudits() {
    if (busy) return;
    const confirm = window.confirm(
      'Backfill audits.account_id?\n\n' +
      'Looks up via properties.uuid → properties.id → accounts.\n' +
      'Skips "Other" audits (property_id NULL, account_id legitimately NULL).\n' +
      'Idempotent. Required before flipping accounts_shadow_read_audits.'
    );
    if (!confirm) return;
    setBusy(true);
    setReconcileMsg('Backfilling audits…');
    try {
      const r = await api.backfillAuditsAccountIds();
      setReconcileMsg(
        r.ok
          ? `Backfill complete — scanned ${r.scanned}, updated ${r.updated}, missing account ${r.missing_account}.`
          : `Failed: ${r.error}`
      );
      await loadAll();
    } catch (e) {
      setReconcileMsg(`Failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Accounts Migration Console</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Properties → Accounts migration. Read first, flip later. Every flag is reversible.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            onClick={runBackfillRequests}
            disabled={busy}
            style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1' }}
            title="One-shot fill of requests.account_id from accounts.legacy_property_id. Idempotent."
          >
            {busy ? 'Working…' : 'Backfill Requests'}
          </button>
          <button
            className="btn btn-primary"
            onClick={runBackfillExpenses}
            disabled={busy}
            style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1' }}
            title="Two-pass backfill of expenses + expense_accounts.account_id. Idempotent."
          >
            {busy ? 'Working…' : 'Backfill Expenses'}
          </button>
          <button
            className="btn btn-primary"
            onClick={runBackfillAudits}
            disabled={busy}
            style={{ background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1' }}
            title="Backfill audits.account_id via property UUID lookup. Idempotent."
          >
            {busy ? 'Working…' : 'Backfill Audits'}
          </button>
          <button
            className="btn btn-primary"
            onClick={runReconcile}
            disabled={busy}
            style={{ background: '#0f172a' }}
          >
            {busy ? 'Working…' : 'Run Reconcile Now'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {reconcileMsg && <div className="alert alert-info" style={{ marginBottom: 16 }}>{reconcileMsg}</div>}

      {/* Stats */}
      {stats && (
        <section style={{ marginBottom: 28 }}>
          <h3 style={sectionTitle}>Counts</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <Stat label="Accounts (total)"  value={stats.accounts.total} />
            <Stat label="Accounts (active)" value={stats.accounts.active}   color="#10b981" />
            <Stat label="Accounts (prospect)" value={stats.accounts.prospect} color="#3b82f6" />
            <Stat label="Accounts (churned)" value={stats.accounts.churned}  color="#94a3b8" />
            <Stat label="Properties (active)" value={stats.properties.active} color="#0f172a" />
            <Stat label="ALN matched"       value={stats.accounts.matched_aln}   color="#10b981" />
            <Stat label="ALN unmatched"     value={stats.accounts.unmatched_aln} color="#f59e0b" />
            <Stat label="Manual accounts"   value={stats.accounts.manual}        color="#7c3aed" />
            <Stat label="Drift (7d)"        value={stats.recent_drift_7d}        color={stats.recent_drift_7d > 0 ? '#dc2626' : '#10b981'} />
          </div>
        </section>
      )}

      {/* Flags */}
      <section style={{ marginBottom: 28 }}>
        <h3 style={sectionTitle}>Migration Flags</h3>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Key</th>
                <th>Description</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 120 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {flags.map(f => (
                <tr key={f.key}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{f.key}</td>
                  <td style={{ fontSize: 12, color: '#475569' }}>{f.description}</td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: f.enabled ? '#10b98118' : '#94a3b818',
                      color:      f.enabled ? '#059669'   : '#64748b',
                    }}>
                      {f.enabled ? 'ON' : 'OFF'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm"
                      disabled={busy}
                      onClick={() => toggleFlag(f.key, !f.enabled)}
                      style={{
                        background: f.enabled ? '#fff' : '#0f172a',
                        color:      f.enabled ? '#dc2626' : '#fff',
                        border:     f.enabled ? '1px solid #fecaca' : 'none',
                      }}
                    >
                      {f.enabled ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Audit log */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ ...sectionTitle, marginBottom: 0 }}>Audit Log <span style={{ fontWeight: 400, color: '#94a3b8' }}>({log.total} total)</span></h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: '#64748b' }}>Severity:</label>
            <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}>
              <option value="">All</option>
              <option value="info">Info</option>
              <option value="warn">Warn</option>
              <option value="error">Error</option>
            </select>
            <button className="btn btn-sm" onClick={loadAll}>Refresh</button>
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 170 }}>When</th>
                <th style={{ width: 70 }}>Sev</th>
                <th style={{ width: 230 }}>Event</th>
                <th>Payload</th>
              </tr>
            </thead>
            <tbody>
              {log.entries.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No entries</td></tr>
              )}
              {log.entries.map(e => (
                <tr key={e.id}>
                  <td style={{ fontSize: 11, color: '#64748b' }}>{formatDateTime(e.created_at)}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                      background: SEVERITY_BG[e.severity] || '#94a3b818',
                      color: SEVERITY_FG[e.severity] || '#64748b',
                    }}>
                      {e.severity?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{e.event_type}</td>
                  <td>
                    {e.payload && (
                      <pre style={{
                        margin: 0, fontSize: 10, lineHeight: 1.4, color: '#475569',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        maxHeight: 120, overflow: 'auto',
                      }}>
                        {JSON.stringify(e.payload, null, 2)}
                      </pre>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const sectionTitle = {
  margin: '0 0 12px',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const SEVERITY_BG = { info: '#3b82f618', warn: '#f59e0b22', error: '#dc262622' };
const SEVERITY_FG = { info: '#2563eb', warn: '#b45309', error: '#b91c1c' };

function Stat({ label, value, color = '#0f172a' }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '14px 16px',
      border: '1px solid #e2e8f0', textAlign: 'center',
    }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    </div>
  );
}
